'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useTheme } from '@/context/ThemeContext';

const NOTIFICATIONS = [
  { id: 'notif-001', title: 'Vendor response received', desc: 'TechServ Arabia submitted offer for PRO-2024-089', time: '12 min ago', read: false, type: 'success' },
  { id: 'notif-002', title: 'Approval pending', desc: 'PRO-2024-091 awaiting Finance approval', time: '1 hr ago', read: false, type: 'warning' },
  { id: 'notif-003', title: 'Deadline approaching', desc: 'PRO-2024-085 vendor RFQ deadline in 4 hours', time: '2 hr ago', read: false, type: 'danger' },
  { id: 'notif-004', title: 'Mission Won 🎉', desc: 'PRO-2024-077 marked as Won — ADNOC Energy', time: '5 hr ago', read: true, type: 'success' },
  { id: 'notif-005', title: 'New mission assigned', desc: 'PRO-2024-093 assigned to you by Khalid Al-Rashid', time: 'Yesterday', read: true, type: 'info' },
];

interface TopbarProps {
  pageTitle: string;
  collapsed: boolean;
}

export default function Topbar({ pageTitle, collapsed }: TopbarProps) {
  const [showNotifs, setShowNotifs] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const unread = NOTIFICATIONS.filter((n) => !n.read).length;
  const isLight = theme === 'light';

  return (
    <header
      className={`fixed top-0 right-0 h-14 flex items-center justify-between px-4 z-20 content-transition`}
      style={{
        left: collapsed ? '64px' : '240px',
        background: isLight ? 'rgba(255,255,255,0.95)' : 'rgba(6, 12, 24, 0.95)',
        backdropFilter: 'blur(12px)',
        borderBottom: isLight ? '1px solid #E2E8F0' : '1px solid rgba(30, 144, 255, 0.15)',
        transition: 'background 300ms ease, border-color 300ms ease',
      }}
    >
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
          <h1 className="text-sm font-600" style={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>{pageTitle}</h1>
        </div>
        <span className="text-xs hidden md:block" style={{ color: isLight ? '#64748B' : '#6B7FA3' }}>SYSTEM ONLINE · Jun 24, 2026</span>
      </div>
      <div className="flex items-center gap-2">
        {/* Search */}
        <div className="relative hidden md:flex items-center">
          <Icon name="MagnifyingGlassIcon" size={14} className="absolute left-2.5" style={{ color: isLight ? '#64748B' : '#6B7FA3' } as React.CSSProperties} />
          <input
            type="text"
            placeholder="Search missions..."
            className="pl-8 pr-3 py-1.5 text-xs rounded-lg w-48 transition-all outline-none"
            style={{
              background: isLight ? '#F1F5F9' : 'rgba(13, 24, 41, 0.8)',
              border: isLight ? '1px solid #E2E8F0' : '1px solid rgba(30, 144, 255, 0.2)',
              color: isLight ? '#0F172A' : '#E8EDF5',
            }}
          />
        </div>

        {/* System status */}
        <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-lg" style={{ background: 'rgba(0, 214, 143, 0.1)', border: '1px solid rgba(0, 214, 143, 0.2)' }}>
          <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
          <span className="text-[10px] font-600 text-green-500">ALL SYSTEMS GO</span>
        </div>

        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg transition-all duration-300"
          title={isLight ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
          style={{
            background: isLight ? '#F1F5F9' : 'rgba(30, 144, 255, 0.1)',
            border: isLight ? '1px solid #E2E8F0' : '1px solid rgba(30, 144, 255, 0.2)',
          }}
        >
          {isLight ? (
            <Icon name="MoonIcon" size={16} style={{ color: '#1E90FF' } as React.CSSProperties} />
          ) : (
            <Icon name="SunIcon" size={16} style={{ color: '#F59E0B' } as React.CSSProperties} />
          )}
        </button>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => setShowNotifs(!showNotifs)}
            className="relative p-2 rounded-lg transition-all"
            style={{ background: showNotifs ? 'rgba(30, 144, 255, 0.15)' : 'transparent' }}
          >
            <Icon name="BellIcon" size={17} style={{ color: isLight ? '#64748B' : '#6B7FA3' } as React.CSSProperties} />
            {unread > 0 && (
              <span
                className="absolute top-1 right-1 w-4 h-4 text-white text-[9px] font-700 rounded-full flex items-center justify-center"
                style={{ background: '#FF4757', boxShadow: '0 0 8px rgba(255,71,87,0.6)' }}
              >
                {unread}
              </span>
            )}
          </button>
          {showNotifs && (
            <div
              className="absolute right-0 top-11 w-80 rounded-xl shadow-modal z-50 fade-in"
              style={{
                background: isLight ? '#FFFFFF' : '#0B1628',
                border: isLight ? '1px solid #E2E8F0' : '1px solid rgba(30, 144, 255, 0.2)',
                boxShadow: isLight ? '0 8px 32px rgba(0,0,0,0.12)' : '0 8px 32px rgba(0,0,0,0.4)',
              }}
            >
              <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: isLight ? '1px solid #E2E8F0' : '1px solid rgba(30, 144, 255, 0.15)' }}>
                <span className="text-sm font-600" style={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>Intel Feed</span>
                <span className="text-xs" style={{ color: '#F59E0B' }}>{unread} active alerts</span>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {NOTIFICATIONS.map((n) => (
                  <div
                    key={n.id}
                    className="px-4 py-3 cursor-pointer transition-colors"
                    style={{
                      borderBottom: isLight ? '1px solid #F1F5F9' : '1px solid rgba(30, 144, 255, 0.08)',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = isLight ? '#F8FAFC' : '#0D1829')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <div className="flex items-start gap-2">
                      <span
                        className="mt-0.5 w-2 h-2 rounded-full shrink-0"
                        style={{
                          background: n.type === 'success' ? '#00D68F' : n.type === 'warning' ? '#F59E0B' : n.type === 'danger' ? '#FF4757' : '#1E90FF',
                          boxShadow: `0 0 6px ${n.type === 'success' ? 'rgba(0,214,143,0.5)' : n.type === 'warning' ? 'rgba(245,158,11,0.5)' : n.type === 'danger' ? 'rgba(255,71,87,0.5)' : 'rgba(30,144,255,0.5)'}`,
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-600" style={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>{n.title}</p>
                        <p className="text-xs mt-0.5 truncate" style={{ color: isLight ? '#64748B' : '#6B7FA3' }}>{n.desc}</p>
                        <p className="text-[10px] mt-1" style={{ color: isLight ? '#94A3B8' : '#4A5568' }}>{n.time}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-4 py-2" style={{ borderTop: isLight ? '1px solid #E2E8F0' : '1px solid rgba(30, 144, 255, 0.15)' }}>
                <button className="text-xs font-600" style={{ color: '#1E90FF' }}>View all intel →</button>
              </div>
            </div>
          )}
        </div>

        {/* User avatar */}
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-700 cursor-pointer"
          style={{ background: 'linear-gradient(135deg, #1E90FF, #0066CC)', boxShadow: '0 0 12px rgba(30,144,255,0.4)' }}
        >
          AM
        </div>
      </div>
    </header>
  );
}